import { useState, useMemo } from 'react';
import { Ship } from 'lucide-react';
import { cn } from '@/lib/utils';
import { mareWidget } from '@/lib/widgets';
import { useTranslation } from '@/hooks/useTranslation';
import { useI18n } from '@/hooks/useI18n';
import {
  clampMes,
  clampDia,
  getHojeSnapshot,
  getDiasVisiveis,
  getAnosDisponiveis,
  interpretarDias,
} from '@/lib/mare-utils';
import mareData from '@/data/mare-2026.json';
import type { MareWidgetVariant } from '@/types/mare';
import { DiaPrincipalCard } from './mare/DiaPrincipalCard';
import { MareTable } from './mare/MareTable';
import { MareCta } from './mare/MareCta';

interface MareWidgetProps {
  mes?: number;
  dia?: number;
  variant?: MareWidgetVariant;
  showCta?: boolean;
  className?: string;
}

export function MareWidget({
  mes,
  dia,
  variant = 'full',
  showCta = true,
  className,
}: MareWidgetProps) {
  const { t } = useTranslation();
  const { lang } = useI18n();

  const [hojeTimestamp] = useState(() => getHojeSnapshot());
  const hoje = useMemo(() => new Date(hojeTimestamp), [hojeTimestamp]);

  const anosDisponiveis = useMemo(() => getAnosDisponiveis(mareData.dados), []);
  const anoReferencia = anosDisponiveis[0] ?? 2026;

  const mesEfetivo = clampMes(mes ?? hoje.getMonth() + 1, 5);
  const diaEfetivo = clampDia(dia ?? hoje.getDate(), mesEfetivo, anoReferencia, 14);

  const anoAtual = hoje.getFullYear();
  const anoCoberto = anosDisponiveis.includes(anoAtual);

  const diasVisiveis = useMemo(
    () => getDiasVisiveis(mareData.dados, mesEfetivo, diaEfetivo, anoAtual),
    [mesEfetivo, diaEfetivo, anoAtual]
  );

  const interpretados = useMemo(
    () => interpretarDias(diasVisiveis, t),
    [diasVisiveis, t]
  );

  const diaPrincipal = interpretados[0];

  if (!anoCoberto) {
    return (
      <section
        className={cn(
          'bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden p-6 text-center',
          className
        )}
        {...mareWidget.getDataAttrs()}
      >
        <p className="font-body text-sm text-warm-600">{t('widget.mare.noData')}</p>
        <p className="font-body text-xs text-warm-gray mt-2">
          {t('widget.mare.noDataYear', {
            anos: anosDisponiveis.join(', '),
            atualizado: mareData.meta.atualizadoEm,
          })}
        </p>
      </section>
    );
  }

  if (!diaPrincipal) {
    return (
      <section
        className={cn(
          'bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden p-6 text-center',
          className
        )}
        {...mareWidget.getDataAttrs()}
      >
        <p className="font-body text-sm text-warm-600">{t('widget.mare.noData')}</p>
      </section>
    );
  }

  return (
    <section
      className={cn(
        'bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden',
        className
      )}
      {...mareWidget.getDataAttrs()}
    >
      <div className="bg-gradient-to-r from-ocean to-teal-deep px-6 py-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Ship className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg tracking-wide">{t('widget.mare.title')}</h3>
            <p className="font-body text-xs text-teal-100">{t('widget.mare.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        <DiaPrincipalCard dia={diaPrincipal} t={t} />

        {variant === 'full' && (
          <MareTable dias={interpretados} t={t} mesRef={mesEfetivo} diaRef={diaEfetivo} />
        )}

        {showCta && <MareCta diaPrincipal={diaPrincipal} t={t} lang={lang} />}

        {variant === 'full' && (
          <p className="font-body text-[11px] text-warm-gray text-center leading-relaxed">
            {t('widget.mare.disclaimer', {
              fonte: mareData.meta.fonte,
              atualizado: mareData.meta.atualizadoEm,
            })}
          </p>
        )}
      </div>
    </section>
  );
}
